import axiosOrig, {
  AxiosAdapter as OrigAxiosAdaptor,
  AxiosDefaults as OrigAxiosDefaults,
  AxiosError as OrigAxiosError,
  AxiosInstance as OrigAxiosInstance,
  AxiosInterceptorManager,
  AxiosPromise as OrigAxiosPromise,
  AxiosRequestConfig as OrigAxiosRequestConfig,
  AxiosResponse as OrigAxiosResponse,
  Method as OrigMethod,
} from 'axios';
import {
  addSASRequestHeader,
  addCsrfHeader,
  createCsrfRetryHandler,
  throttleGate,
  throttleRelease,
  throttleReleaseError,
  addRequestedWithHeader,
  verifyAuthentication,
  captureCsrfHeader,
} from './interceptors';

import { UserPrefCache } from './cache/prefCache';
import { CacheManager } from './cache';
import { IdentitiesCache } from './cache/identitiesCache';
import { addAcceptLocaleHeader, setAcceptLocaleHeader } from './interceptors/accept-locale-header';
import { sleep } from '../util';
interface NovaAxiosConfig {
  /**
   * When defined with a non-empty string, a header named SAS-Request is included on the request
   * to assist with request tracing. A total and an individual trace count is maintained for each
   * traceId. Both are sent in the header. The format of the header value is
   * '<traceId>;<individual count>;<total count>'
   */
  traceId?: string;
  /**
   * Set to true when the request should ignore the service throttle. The throttle should only
   * wait for the first request to complete and then opens up for multiple requests at a time.
   */
  disableNovaServiceThrottle?: boolean;

  // cache is used to explicitly by pass cache implementation, if any, associated
  // with the call.
  // Set to false to by pass the cache.
  cache?: boolean;

  /**
   * Number of times to retry CSRF failures.
   */
  csrfRetryLimit?: number;

  /**
   * For internal use with CSRF hander. Consumers probably want `csrfRetryLimit` instead.
   */
  csrfRetryCount?: number;
}
export interface AxiosDefaults extends OrigAxiosDefaults, NovaAxiosConfig {}
export interface AxiosRequestConfig extends OrigAxiosRequestConfig, NovaAxiosConfig {}

export interface AxiosAdapter extends OrigAxiosAdaptor {
  (config: AxiosRequestConfig): AxiosPromise<any>;
}

export interface AxiosResponse<T = any> extends OrigAxiosResponse<T> {
  config: AxiosRequestConfig;
}

export interface AxiosError<T = any> extends OrigAxiosError {
  config: AxiosRequestConfig;
  response?: AxiosResponse<T>;
}

export interface AxiosPromise<T = any> extends OrigAxiosPromise<T> {}

export interface AxiosInstance extends OrigAxiosInstance {
  (config: AxiosRequestConfig): AxiosPromise;
  (url: string, config?: AxiosRequestConfig): AxiosPromise;
  defaults: AxiosDefaults;
  interceptors: {
    request: AxiosInterceptorManager<AxiosRequestConfig>;
    response: AxiosInterceptorManager<AxiosResponse>;
  };
  getUri(config?: AxiosRequestConfig): string;
  request<T = any, R = AxiosResponse<T>>(config: AxiosRequestConfig): Promise<R>;
  get<T = any, R = AxiosResponse<T>>(url: string, config?: AxiosRequestConfig): Promise<R>;
  delete<T = any, R = AxiosResponse<T>>(url: string, config?: AxiosRequestConfig): Promise<R>;
  head<T = any, R = AxiosResponse<T>>(url: string, config?: AxiosRequestConfig): Promise<R>;
  options<T = any, R = AxiosResponse<T>>(url: string, config?: AxiosRequestConfig): Promise<R>;
  post<T = any, R = AxiosResponse<T>>(
    url: string,
    data?: any,
    config?: AxiosRequestConfig
  ): Promise<R>;
  put<T = any, R = AxiosResponse<T>>(
    url: string,
    data?: any,
    config?: AxiosRequestConfig
  ): Promise<R>;
  patch<T = any, R = AxiosResponse<T>>(
    url: string,
    data?: any,
    config?: AxiosRequestConfig
  ): Promise<R>;
}

const createNovaAxios = (config?: AxiosRequestConfig): AxiosInstance => {
  const axios = axiosOrig.create(config);

  axios.interceptors.request.use(addRequestedWithHeader);
  axios.interceptors.response.use(undefined, verifyAuthentication);

  axios.interceptors.request.use(addSASRequestHeader);

  // The order of the addCsrfHeader and throttle gate are meaningful here.
  // If multiple requests are going to the same service, we want the first one to get the CSRF
  // header before trying to add it to the other requests. So the gate must run before the header
  // injection. The current implementatin of axios has request interceptors run in reverse order.
  axios.interceptors.request.use(addCsrfHeader);
  axios.interceptors.response.use(captureCsrfHeader, createCsrfRetryHandler(axios));

  axios.interceptors.request.use(throttleGate);
  axios.interceptors.response.use(throttleRelease, throttleReleaseError);

  axios.interceptors.request.use(addAcceptLocaleHeader);

  return axios;
};

// CacheAwareAxios returns cache aware instance of axios that can route calls
// through cache, if applicable, before handing over to input axios instance.
const cacheAwareAxios = (_axios: AxiosInstance): AxiosInstance => {
  let cacheCreated = false;
  const createCache = async () => {
    cacheCreated = true;
    try {
      const { getConfiguration } = await import('../internal');
      const config = await getConfiguration();
      if (config.get('nova-axios-cache') === false) {
        return;
      }
    } catch (err) {
      // ignore
    }
    CacheManager.instance.addCache(new UserPrefCache(_axios));
    CacheManager.instance.addCache(new IdentitiesCache(_axios));
  };
  // TODO this axios api should be deprecated.
  const x: any = (url: any, config?: any): AxiosPromise => {
    if (typeof url !== 'string') {
      return x.request(url);
    }
    if (config) {
      config.url = url;
      return x.request(config);
    }
    return x.get(url);
  };
  x.defaults = _axios.defaults;
  x.interceptors = _axios.interceptors;
  x.getUri = (config?: AxiosRequestConfig): string => {
    return _axios.getUri(config);
  };
  x.request = <T = any, R = AxiosResponse<T>>(config: AxiosRequestConfig): Promise<R> => {
    // delay cache activation until very first call so configurations can be read to disable
    // cache.
    if (!cacheCreated) {
      createCache();
    }
    const cache = CacheManager.instance.findCache(config);
    if (cache) {
      return cache.handle(config);
    }
    return _axios.request(config);
  };

  const noDataMethods = <T = any, R = AxiosResponse<T>>(
    method: OrigMethod,
    url: string,
    config?: AxiosRequestConfig
  ): Promise<R> => {
    const cnf = config || {};
    cnf.url = url;
    cnf.method = method;
    return x.request(cnf);
  };

  const dataMethods = <T = any, R = AxiosResponse<T>>(
    method: OrigMethod,
    url: string,
    data?: any,
    config?: AxiosRequestConfig
  ): Promise<R> => {
    const cnf = config || {};
    cnf.url = url;
    cnf.method = method;
    cnf.data = data;
    return x.request(cnf);
  };

  x.get = <T = any, R = AxiosResponse<T>>(url: string, config?: AxiosRequestConfig): Promise<R> => {
    return noDataMethods('get', url, config);
  };

  x.delete = <T = any, R = AxiosResponse<T>>(
    url: string,
    config?: AxiosRequestConfig
  ): Promise<R> => {
    return noDataMethods('delete', url, config);
  };

  x.head = <T = any, R = AxiosResponse<T>>(
    url: string,
    config?: AxiosRequestConfig
  ): Promise<R> => {
    return noDataMethods('head', url, config);
  };

  x.options = <T = any, R = AxiosResponse<T>>(
    url: string,
    config?: AxiosRequestConfig
  ): Promise<R> => {
    return noDataMethods('options', url, config);
  };

  x.post = <T = any, R = AxiosResponse<T>>(
    url: string,
    data?: any,
    config?: AxiosRequestConfig
  ): Promise<R> => {
    return dataMethods('post', url, data, config);
  };

  x.put = <T = any, R = AxiosResponse<T>>(
    url: string,
    data?: any,
    config?: AxiosRequestConfig
  ): Promise<R> => {
    return dataMethods('put', url, data, config);
  };

  x.patch = <T = any, R = AxiosResponse<T>>(
    url: string,
    data?: any,
    config?: AxiosRequestConfig
  ): Promise<R> => {
    return dataMethods('patch', url, data, config);
  };
  return x;
};

/**
 * Create a cache-aware instance of Nova Axios with the provided config. The config options become the default
 * for all requests made with the returned instance.
 * @param config Config to pass to initialize Axios instance
 * @returns Nova Axios instance
 */
export const createAxiosInstance = (config?: AxiosRequestConfig) => {
  return cacheAwareAxios(createNovaAxios(config));
};

const axios = createAxiosInstance();

// declare global variable to share cache across apps.
declare global {
  // eslint-disable-next-line no-var
  var sas: {
    cacheManager: CacheManager;
    axiosPreventFetchAcceptLocale?: boolean;
    runAxiosFetchInTest?: boolean;
  };
}
window.sas = window.sas || {};
// a possibility when nova code is embedded in openui app.
if (!window.sas.cacheManager) {
  // delay creation of cache manager.
  // TODO disable cache manager based on platform property in config.
  Object.defineProperty(window.sas, 'cacheManager', {
    get: function () {
      return CacheManager.instance;
    },
  });
}

export let axiosReady = Promise.resolve();

// Most tests that use axios will break if it sends this extra request.
// Some tests even seem to be affected the extra async logic ran here.
// So avoid running this in tests unless explicitly told to.
// Using runAxiosFetchInTest also requires lazy importing axios in the test.
if (process.env.NODE_ENV !== 'test' || window.sas.runAxiosFetchInTest) {
  // Fetch preferences to initialize Accept-Locale header
  // Run the fetch here since this is the only place where we will have
  // the cacheAwareAxios available. The cache is important to reduce the
  // number of duplicate calls on app startup.
  axiosReady = new Promise<void>(async (resolve) => {
    await sleep(0);
    if (window.sas.axiosPreventFetchAcceptLocale) return;
    const response = await axios.get('/preferences/preferences/@currentUser');
    for (const setting of response.data.items) {
      if (setting.id === 'FormatLocale.DefaultLocale') {
        setAcceptLocaleHeader(setting.value);
        break;
      }
    }
    resolve();
  });
}

export default axios;
